import * as $ from "jquery";
import * as _ from "lodash";
import * as moment from "moment-timezone";
import "select2";
import { getIAQChannelType } from "../../../../../../common/channelUtils";
import { DashboardChannel } from "../../../../models/DashboardChannel";
import { GetSeriesOptions } from "../../../dashboard/widgets/ChartWorkerManager";
import {
    BaseDashboardWidgetView,
    BaseDashboardWidgetViewOptions,
} from "../../../dashboard/widgets/_BaseWidgetView";
import {
    CHART_AGGREGATION_PERIOD,
    CHART_AGGREGATION_TYPE,
    CHART_DATA_RANGE,
    CHART_SIZE,
    CHART_TYPE,
    ChartConfig,
    ChartHelper,
    minMaxTimeStampOptions
} from "../../../dashboard/widgets/chart/ChartHelper";
import { getAggFunctionName } from "../../ReportUtils";

declare const App: any;

export class AggregateTableWidgetView extends BaseDashboardWidgetView {
    protected template = _.template(
        require("templates/reports/widgets/table/table.html")
    );

    private aggFnByChannelType: Map<string, number[]>= new Map();
    private aggTypes;
    private columnsNames: string[] = [];
    private MAX_CHANNELS_TO_SHOW = 35;
    private MAX_COLUMNS_TO_SHOW = 8;
    private minDataRequested: number = Infinity;
    private maxDataRequested: number = null;
    private orderByChannelTypeAndAggType: Map<Array<string>, number> = new Map();
    private tableChannels: Backbone.Model[];
    private tableDashboardChannelIds: number[];
    private tableData: { [dashboadChannelId: number]: any };
    private totalledDashboardChannelIds = [];

    constructor(options: BaseDashboardWidgetViewOptions) {
        super(
            <any>_.extend(
                {
                    tagName: "div",
                    className: "widget",
                    events: {
                        "input .chart-data-range input":
                            "handleTimePickerChange",
                        "dp.change .chart-data-range .date":
                            "handleTimePickerChange",
                        "click .chart-data-range .reset":
                            "handleTimeSelectorReset",

                        // Widget related buttons
                        "click .w-settings .remove": "handleRemoveWidgetClick",
                        "click .w-delete-widget .w-cancel":
                            "handleRemoveWidgetCancelClick",
                        "click .w-delete-widget .w-confirm":
                            "handleRemoveWidgetConfirmClick",
                        "click .w-settings .gear": "handleEditWidgetClick",
                        "click .w-settings .clone": "handleCloneWidget",
                    },
                },
                options
            )
        );
    }

    render() {
        this.$el.html(
            this.template({
                widget: this.options.widget,
                reportId: this.options.report?.get("id"),
                permissionCollection: this.options.permissionCollection,
                editable: this.options.editable,
                movable: this.options.movable,
                cloneable: this.options.cloneable,
                deletable: this.options.deletable,
            })
        );

        this.$el.addClass(
            "widget-w-" +
                this.options.widget.getParamValue(
                    "CHART_SIZE",
                    CHART_SIZE.SIZE_6
                ) + " aggregate-widget"
        );
        this.$el.attr("data-widget-id", this.options.widget.get("id"));

        this.tableData = {};
        // Get channels for this table
        const widgetId = this.options.widget.get("id");
        this.tableChannels = <DashboardChannel[]>(
            this.options.dashboardChannelCollection.where({
                dashboard_widget_id: widgetId,
            })
        );
        this.tableDashboardChannelIds = _.map(this.tableChannels, (channel) =>
            channel.get("dashboard_channel_id")
        );

        // Get aggregation types values
        this.aggTypes = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', '').split(',');

        // generate column/summary labels from channels
        const columnsNames: string[] = [];
        const aggFnByChannelType: Map<string, number[]>= new Map();
        for (const dchannel of this.tableChannels) {
            const dchannelOrder = dchannel.get("dashboard_channel_order");
            const aggFunctionName = getAggFunctionName(this.aggTypes[dchannelOrder]);

            // Only render the column for same channel type and agg type
            const sensorType = dchannel.get('sensor_type');
            const sensorVersion = dchannel.get('sensor_version');
            const channelPosition = dchannel.get('channel_position')
            const isVirtual = dchannel.get('channel_virtual');
            const channelType = getIAQChannelType(sensorType, sensorVersion, channelPosition, isVirtual);
            if (channelType) {
                if (aggFnByChannelType.has(channelType)) {
                    const aggTypesForChannelType = aggFnByChannelType.get(channelType);
                    const isPresent = Number(aggTypesForChannelType?.indexOf(this.aggTypes[dchannelOrder]));
                    
                    if(isPresent > -1) {
                        continue;
                    }
                } else { // this is the first chanel type, add it
                    aggFnByChannelType.set(channelType, []);
                }
                aggFnByChannelType.get(channelType)?.push(this.aggTypes[dchannelOrder]);

                const columnName = `${dchannel.get("dashboard_channel_name")} 
                            - ${dchannel.get("channel_unit")} 
                            (${_.escape(aggFunctionName)})`;
                columnsNames.push(columnName);
            }
        }

        // If there are too many channels or the table has too many columns,
        // don't fetch the data and render the summary instead
        const tooManyChannels = this.tableChannels.length > this.MAX_CHANNELS_TO_SHOW;
        const tooManyColumns = columnsNames.length > this.MAX_COLUMNS_TO_SHOW;
        if (tooManyChannels || tooManyColumns) {
            setTimeout(() => {
                this.renderTableSummary(columnsNames);
            }, 200)
        } else {
            $.when(this.getData(this.tableDashboardChannelIds)).then(() => {
                this.renderTableColumnsAndRows();
            });
        }

        this.delegateEvents();
        return this;
    }

    private setLoading(loading: boolean) {
        this.$(".w-table .modal-form-spinner").toggleClass("hidden", !loading);
        this.$(".w-table .table").toggleClass("hidden", loading);
    }

    private setTimestamps(dashboardChannelIds) {
        const aggregationParam = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', CHART_AGGREGATION_TYPE.DEFAULT);
        const aggregationTypes = _.map(_.split(aggregationParam, ','));
        const isPeriodDelta = _.includes(aggregationTypes, CHART_AGGREGATION_TYPE.MAXDIFF);

        _.each(dashboardChannelIds, (id) => {
            const channel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: Number(id) });
            const initial = this.minDataRequested === Infinity;

            const baseMinMaxOptions = {
                initial: initial,
                channel: channel,
                widget: this.options.widget,
                customDataRangeFunc: null,
                isDefaultChart: false,
                dashboardChannelCollection: this.options.dashboardChannelCollection,
                graphedDashboardChannelIds: dashboardChannelIds,
                user: this.options.currentUser
            };
            const maxOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
                maxDataRequested: this.maxDataRequested
            });
            const max = ChartHelper.getMaxTimestampToRequest(maxOptions);
            if (max > this.maxDataRequested) this.maxDataRequested = max;

            const minOptions: minMaxTimeStampOptions = _.extend(baseMinMaxOptions, {
                minDataRequested: this.minDataRequested,
                maxDataRequested: this.maxDataRequested
            });
            const min = ChartHelper.getMinTimestampToRequest(minOptions);
            if (min < this.minDataRequested) this.minDataRequested = min;
        });

        // request enough data for period delta to make a dif between two points
        // TODO remove this hack and add logic to .processData in chartworker somehow.
        if (isPeriodDelta) {
            const diffTime = this.maxDataRequested - this.minDataRequested;
            this.minDataRequested = this.minDataRequested - diffTime;
        }
    }

    /** CHART_DATA_RANGE values that use CHART_AGGREGATION_PERIOD.RANGE (1 Day, 1 Week, 1 Month, 3 Months, 6 Months, 1 Year) */
    private static readonly RANGE_PERIOD_DATA_RANGE_VALUES = [
        CHART_DATA_RANGE.DAY_1,
        CHART_DATA_RANGE.WEEK_1,
        CHART_DATA_RANGE.MONTH_1,
        CHART_DATA_RANGE.MONTH_3,
        CHART_DATA_RANGE.MONTH_6,
        CHART_DATA_RANGE.YEAR_1,
    ];

    getData(dashboardChannelIds: number[]) {
        this.setTimestamps(dashboardChannelIds);

        const deferred = $.Deferred();
        const widgetPeriod = this.options.widget.getParamValue('CHART_AGGREGATION_PERIOD', CHART_AGGREGATION_PERIOD.DEFAULT);
        const dataRange = this.options.widget.getParamValue('CHART_DATA_RANGE', '');
        const useRangePeriod = AggregateTableWidgetView.RANGE_PERIOD_DATA_RANGE_VALUES.indexOf(String(dataRange)) !== -1;
        const period = useRangePeriod ? CHART_AGGREGATION_PERIOD.RANGE : widgetPeriod;
        const aggregationParam = this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', CHART_AGGREGATION_TYPE.DEFAULT);

        const chartConfig: ChartConfig = {
            seriesGroups: [{
                type: CHART_TYPE.WIDGET_MULTI_SERIES_SINGLE_POINT,
                period: period,
                series: _.map(dashboardChannelIds, (dashboardChannelId) => {
                    const dashboardChannel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: dashboardChannelId });
                    const aggregationTypes = _.map(_.split(aggregationParam, ','));
                    const order = dashboardChannel.get('dashboard_channel_order');


                    // get agg type by dashboard channel order
                    let aggType;
                    if (widgetPeriod === CHART_AGGREGATION_PERIOD.NONE) {
                        aggType = CHART_AGGREGATION_TYPE.NONE;
                    } else if (order && order < aggregationTypes.length) {
                        aggType = aggregationTypes[order];
                    } else {
                        aggType = aggregationTypes[0];
                    }

                    return {
                        agg: aggType,
                        tag: dashboardChannelId,
                        channelId: dashboardChannel.get('channel_id'),
                        name: dashboardChannel.get('channel_name')
                    }
                })
            }],
            timezone: ChartHelper.getChartTimezone(this.options.widget, this.options.currentUser),
            showThresholdPlotlines: false,
            realTimestamps: false,
            hideSingleGaps: true,
            convertAllValues: true,
            labels: false
        };

        const getSeriesChannels = {};
        _.each(dashboardChannelIds, (dashboardChannelId) => {
            const dashboardChannel = this.options.dashboardChannelCollection.findWhere({ dashboard_channel_id: dashboardChannelId });
            getSeriesChannels[dashboardChannel.get('channel_id')] = {
                id: dashboardChannel.get('channel_id'),
                sensor_type: dashboardChannel.get('sensor_type'),
                sensor_connector: dashboardChannel.get('sensor_connector'),
                sensor_version: dashboardChannel.get('sensor_version'),
                position: dashboardChannel.get('channel_position'),
                channel_secret: dashboardChannel.get('channel_secret'),
                last_timestamp: dashboardChannel.get('channel_last_timestamp') || moment().unix(),
                polling_frequency: dashboardChannel.get('module_polling_frequency'),
                tags: [dashboardChannelId]
            }
        });
         // Fetch one more day's worth of data for period delta channels to compare
         let startTimestamp;
         if (this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', null) &&
             this.options.widget.getParamValue('CHART_AGGREGATION_TYPE', null) === CHART_AGGREGATION_TYPE.MAXDIFF) {
             startTimestamp = this.minDataRequested - 86400
         } else {
             startTimestamp = this.minDataRequested;
         }
        const getSeriesOptions: GetSeriesOptions = {
            chartConfig: chartConfig,
            channels: getSeriesChannels,
            startTimestamp: this.minDataRequested,
            endTimestamp: this.maxDataRequested,
        };
        $.when(
            this.options.chartWorkerManager.getSeries(getSeriesOptions)
        ).then((data) => {
            // Keyed by chart type
            // Multi channel series is located at index 0
            this.tableData = _.extend(
                this.tableData,
                data[CHART_TYPE.WIDGET_MULTI_SERIES_SINGLE_POINT][0]
            );
            _.each(this.tableData, (tableData, dashboardId) => {
                this.tableData[dashboardId] = _.sortBy(tableData, "0");
            });
            deferred.resolve();
        });
        return deferred;
    }

    private renderColumns(rows) {
        const columns = [`<tr class="header"><td class="text-center">Site</td>`];

        // Add column title with name of each channel
        let order = 0;
        for (const dashbaordChannelId in this.tableData) {
            const dchannel = this.options.dashboardChannelCollection.findWhere({
                dashboard_channel_id: Number(dashbaordChannelId),
            });
            const dchannelOrder = dchannel.get("dashboard_channel_order");
            const aggFunctionName = getAggFunctionName(this.aggTypes[dchannelOrder]);

            // Only render the column for same channel type and agg type
            const sensorType = dchannel.get('sensor_type');
            const sensorVersion = dchannel.get('sensor_version');
            const channelPosition = dchannel.get('channel_position')
            const isVirtual = dchannel.get('channel_virtual');
            const channelType = getIAQChannelType(sensorType, sensorVersion, channelPosition, isVirtual);
            if (channelType) {
                if (this.aggFnByChannelType.has(channelType)) {
                    const aggTypesForChannelType = this.aggFnByChannelType.get(channelType);
                    const isPresent = Number(aggTypesForChannelType?.indexOf(this.aggTypes[dchannelOrder]));
                    
                    if(isPresent > -1) {
                        continue;
                    }
                } else { // this is the first chanel type, add it
                    this.aggFnByChannelType.set(channelType, []);
                }
                this.aggFnByChannelType.get(channelType)?.push(this.aggTypes[dchannelOrder]);

                const columnName = `
                ${dchannel.get("dashboard_channel_name")} - 
                ${dchannel.get("channel_unit")} 
                `;
                columns.push(
                    `<td class="text-center"> 
                    ${_.escape(columnName)} 
                    <span class="aggName"> (${_.escape(aggFunctionName)}) </span>
                    </td>`
                    );

                // add column to local reference in case we need to display the summary
                this.columnsNames.push(`${_.escape(columnName)} (${_.escape(aggFunctionName)})`);
                    
                // local reference of the columns order so we can properly render the values for each module
                const key = [channelType, this.aggTypes[dchannelOrder]];
                this.orderByChannelTypeAndAggType.set(key, order);
                order++;
            }
        }

        columns.push(`</tr>`);

        rows.push(columns.join(""));
    }

    private renderRows(rows) {
        const tableRowsByModuleID: Map<string, any>= new Map();

        for (const [[channelType,aggType], _order] of this.orderByChannelTypeAndAggType.entries()) {
            for (const dchannel of this.tableChannels) {
                const sensorType = dchannel.get('sensor_type');
                const sensorVersion = dchannel.get('sensor_version');
                const channelPosition = dchannel.get('channel_position')
                const isVirtual = dchannel.get('channel_virtual');
                const dchannelType = getIAQChannelType(sensorType, sensorVersion, channelPosition, isVirtual);

                const dchannelOrder = dchannel.get("dashboard_channel_order");
                const dchannelAggType = this.aggTypes[dchannelOrder];

                if(channelType === dchannelType && aggType === dchannelAggType) {
                    const dchannelID = dchannel.get("dashboard_channel_id");
                    const install = this.options.manager.options.installCollection.findWhere({
                        id: dchannel.get("install_id"),
                    });
                    const moduleName = dchannel.get("module_location");
                    const moduleNumber = dchannel.get("module_number");
                    const deviceLabel = `${install.getDisplayName()} - ${moduleName}`;

                    if (!tableRowsByModuleID.get(moduleNumber)) {
                        tableRowsByModuleID.set(moduleNumber, [deviceLabel]);
                    }
                    const element = this.tableData[dchannelID];
                    let value: number | string = "";
                    if(element) value = element[1] ? _.round(element[1], 2) : "";
                    tableRowsByModuleID.get(moduleNumber).push(value);
                }
            }
        }

        const templateRows:any = [];
        let values = Array.from(tableRowsByModuleID.values());
        // Sort the rows by the site name (first value) alphabetically
        const sortedValues = values.sort((a, b) => a[0].localeCompare(b[0]));
          
        for (let [i, dataValues] of sortedValues.entries()) {
            let rowString = `<tr>`;
            const dataStrings = _.map(dataValues, (value) => {
                return `<td class="text-center">${_.escape(value)}</td>`;
            });

            rowString += dataStrings.join("");
            rowString += `</tr>`;

            // Longest row should be the colspan for the "Aditional rows" legend
            const colspan = sortedValues.reduce((maxLength, currentArray) => {
                const currentLength = currentArray.length;
                return currentLength > maxLength ? currentLength : maxLength;
            }, 0);
            if (i === 10) {
                rowString = `<tr><td class="text-center" colspan=${colspan}> 
                             And ${values.length - 10} Additional rows</td></tr>`;
            }

            // hide really long tables
            if (i > 10) return;
            templateRows.push(rowString);
        }

        rows.push(templateRows.join(""));
    }

    private renderTableColumnsAndRows() {
        this.setLoading(false);

        // If there is no data, just render empty template
        if(Object.keys(this.tableData).length === 0) {
            const noData =`
                <h3 class="text-center"> IAQ Aggregate Report Table </h3>
                <h4 class="text-center" style="color: #114257;">
                    There is no data available for your selection.
                </h4>`;
            this.$(".w-table .table").html(noData);
        } else {
            const rows = [];
            this.renderColumns(rows);
            this.renderRows(rows);
            this.$(".w-table .table").html(rows.join(""));            
        }

        // Tell Packery to do its magic again after the template is loaded, so that our widgets are re-organized
        App.Bus.trigger(
            "dashboard:category:" + this.options.categoryId + ":layout"
        );
    }

    renderTableSummary(columnsNames: string[]){
        this.setLoading(false);

        const widgetHeader =
        '<h3 class="text-center">Report Table</h3><h4 class="text-center" style="color: #114257;">' +
        _.escape(this.options.widget.getParamValue("CHART_TITLE", "IAQ Aggregate Table")) +
        "</h4>";

        // Sort columns alphabetically
        const sortedColumns = columnsNames.sort();    
        const columnsList = _.map(sortedColumns, (name) => { return (`<li> ${name} </li>` )}).join("");
        this.$(".w-table .table").html(
            widgetHeader +
                '<div class="text-center">' +
                '<h5 style="font-style: italic;">Including channels:</h5>' +
                '<ul style="list-style: none; padding-left: 0;">' +
                columnsList +
                "</ul>" +
                "</div>"
        );

        // Tell Packery to do its magic again after the template is loaded, so that our widgets are re-organized
        App.Bus.trigger(
            "dashboard:category:" + this.options.categoryId + ":layout"
        );
    }

    private getTimezone() {
        return this.options.widget.getParamValue(
            "CHART_TIMEZONE",
            this.options.currentUser.get("timezone")
        );
    }

    remove() {
        // Terminate web workers and unload everything
        this.destroy();
        return super.remove();
    }

    destroy() {
        this.minDataRequested = Infinity;
        this.maxDataRequested = null;
    }
}



// WEBPACK FOOTER //
// ./srv/public/js/views/reports/widgets/aggregatetablewidget/AggregateTableWidgetView.ts